import { useState } from 'react';
import { ChevronDown, Search, MessageCircle, BookOpen, Shield, CreditCard, Globe, Smartphone, Users, Clock, Phone } from 'lucide-react';

export default function Faq() {
  const [activeCategory, setActiveCategory] = useState('general');
  const [openItems, setOpenItems] = useState({});
  const [searchQuery, setSearchQuery] = useState('');

  const faqCategories = [
    { id: 'general', label: 'Général', icon: <Globe size={20} /> },
    { id: 'services', label: 'Services', icon: <Smartphone size={20} /> },
    { id: 'pricing', label: 'Tarifs & Devis', icon: <CreditCard size={20} /> },
    { id: 'process', label: 'Processus', icon: <Users size={20} /> },
    { id: 'technical', label: 'Technique', icon: <BookOpen size={20} /> },
    { id: 'privacy', label: 'Confidentialité', icon: <Shield size={20} /> },
  ];

  const faqItems = {
    general: [
      {
        question: "Quels sont vos délais de réponse ?",
        answer: "Nous nous engageons à répondre à toutes les demandes dans un délai de 24 heures ouvrables. Pour les demandes urgentes, vous pouvez nous appeler directement au +33 1 23 45 67 89.",
        tags: ["délais", "contact", "urgent"]
      },
      {
        question: "Quelles sont vos heures d'ouverture ?",
        answer: "Notre équipe est disponible du lundi au vendredi de 9h00 à 18h00. Le samedi de 10h00 à 14h00 pour les rendez-vous programmés. Vous pouvez nous contacter par email 24h/24.",
        tags: ["horaires", "contact", "disponibilité"]
      },
      {
        question: "Proposez-vous des consultations gratuites ?",
        answer: "Oui, nous offrons une première consultation gratuite de 30 minutes pour discuter de votre projet, analyser vos besoins et vous présenter nos solutions.",
        tags: ["consultation", "gratuit", "devis"]
      },
      {
        question: "Travaillez-vous avec des clients internationaux ?",
        answer: "Absolument ! Nous travaillons avec des clients dans toute l'Europe. Toutes nos communications peuvent se faire en français et en anglais.",
        tags: ["international", "langues", "Europe"]
      }
    ],
    services: [
      {
        question: "Quels types de sites web développez-vous ?",
        answer: "Nous développons : sites vitrines, sites e-commerce (avec paiement en ligne), sites institutionnels, applications web, plateformes SaaS, et bien plus. Chaque projet est sur mesure.",
        tags: ["sites", "e-commerce", "développement"]
      },
      {
        question: "Proposez-vous des services de maintenance ?",
        answer: "Oui, nous proposons différents forfaits de maintenance : basique (mises à jour de sécurité), standard (+ support technique), premium (+ améliorations continues).",
        tags: ["maintenance", "support", "forfaits"]
      },
      {
        question: "Faites-vous du référencement (SEO) ?",
        answer: "Oui, nous proposons des services complets de référencement naturel et payant, incluant audits SEO, optimisation technique, création de contenu et suivi des performances.",
        tags: ["SEO", "référencement", "marketing"]
      },
      {
        question: "Proposez-vous des formations ?",
        answer: "Nous formons vos équipes à l'utilisation de vos nouveaux outils (CMS, CRM, etc.). Des sessions en présentiel ou à distance selon vos besoins.",
        tags: ["formation", "CMS", "assistance"]
      }
    ],
    pricing: [
      {
        question: "Comment sont calculés vos tarifs ?",
        answer: "Nos tarifs sont calculés en fonction de la complexité du projet, du temps de développement, des fonctionnalités demandées et des ressources nécessaires. Nous fournissons toujours un devis détaillé.",
        tags: ["tarifs", "devis", "budget"]
      },
      {
        question: "Proposez-vous des forfaits mensuels ?",
        answer: "Oui, pour la maintenance, l'hébergement et le support. Nous avons des forfaits adaptés aux petites entreprises comme aux grandes structures.",
        tags: ["forfaits", "mensuel", "maintenance"]
      },
      {
        question: "Quels modes de paiement acceptez-vous ?",
        answer: "Virement bancaire, carte de crédit, PayPal. Pour les gros projets, nous proposons un échelonnement des paiements (30% à la commande, 40% à la livraison, 30% à la mise en ligne).",
        tags: ["paiement", "financement", "virement"]
      },
      {
        question: "Y a-t-il des frais cachés ?",
        answer: "Non, tous nos devis sont transparents et détaillés. Tout ce qui est inclus est clairement spécifié. Toute modification en cours de projet fera l'objet d'un avenant.",
        tags: ["transparence", "devis", "frais"]
      }
    ],
    process: [
      {
        question: "Quel est votre processus de travail ?",
        answer: "1. Consultation initiale gratuite\n2. Analyse des besoins et devis\n3. Signature du contrat\n4. Conception et maquettes\n5. Développement\n6. Tests et corrections\n7. Formation et mise en ligne\n8. Support et maintenance",
        tags: ["processus", "étapes", "méthodologie"]
      },
      {
        question: "Combien de temps pour un site vitrine ?",
        answer: "En moyenne 4 à 6 semaines selon la complexité. Cela inclut la conception, le développement, les intégrations et les tests.",
        tags: ["délais", "site vitrine", "calendrier"]
      },
      {
        question: "Puis-je suivre l'avancement de mon projet ?",
        answer: "Oui, vous aurez accès à un tableau de bord en ligne avec l'avancement en temps réel, et des points réguliers avec votre chef de projet.",
        tags: ["suivi", "communication", "transparence"]
      },
      {
        question: "Comment se passent les révisions ?",
        answer: "Nous incluons 3 tours de révisions dans nos devis. Chaque révision est documentée et validée par email pour garantir une parfaite compréhension.",
        tags: ["révisions", "modifications", "feedback"]
      }
    ],
    technical: [
      {
        question: "Quelles technologies utilisez-vous ?",
        answer: "React, Next.js, Node.js, PHP, WordPress, Shopify, MongoDB, PostgreSQL. Nous choisissons les technologies les plus adaptées à votre projet.",
        tags: ["technologies", "React", "WordPress"]
      },
      {
        question: "Proposez-vous l'hébergement ?",
        answer: "Oui, nous proposons des solutions d'hébergement sécurisées et optimisées, avec sauvegardes quotidiennes et certificats SSL inclus.",
        tags: ["hébergement", "SSL", "sécurité"]
      },
      {
        question: "Mes données seront-elles sécurisées ?",
        answer: "Absolument. Nous mettons en place les dernières normes de sécurité : chiffrement, protection contre les attaques, conformité RGPD.",
        tags: ["sécurité", "RGPD", "données"]
      },
      {
        question: "Quelle est votre politique de sauvegarde ?",
        answer: "Sauvegardes quotidiennes automatiques, conservées 30 jours. Pour les sites e-commerce, sauvegardes supplémentaires avant chaque mise à jour.",
        tags: ["sauvegarde", "sécurité", "données"]
      }
    ],
    privacy: [
      {
        question: "Comment protégez-vous mes données personnelles ?",
        answer: "Nous sommes conformes au RGPD. Vos données sont chiffrées, stockées en UE, et nous ne les partageons jamais avec des tiers sans votre consentement.",
        tags: ["RGPD", "confidentialité", "données"]
      },
      {
        question: "Puis-je exporter mes données à tout moment ?",
        answer: "Oui, conformément au RGPD, vous pouvez demander l'export de toutes vos données dans un format standard (JSON, CSV).",
        tags: ["export", "données", "RGPD"]
      },
      {
        question: "Quelle est votre politique de confidentialité ?",
        answer: "Toutes les informations échangées sont strictement confidentielles. Nous signons des accords de confidentialité (NDA) si nécessaire.",
        tags: ["confidentialité", "NDA", "sécurité"]
      },
      {
        question: "Conservez-vous mes données après le projet ?",
        answer: "Nous conservons les données nécessaires à la facturation pendant 10 ans (obligation légale). Les autres données sont supprimées à votre demande ou après 2 ans d'inactivité.",
        tags: ["conservation", "données", "suppression"]
      }
    ]
  };

  const toggleItem = (category, index) => {
    const key = `${category}-${index}`;
    setOpenItems(prev => ({
      ...prev,
      [key]: !prev[key]
    }));
  };

  const filteredItems = Object.keys(faqItems).reduce((acc, category) => {
    const items = faqItems[category].filter(item => 
      item.question.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.answer.toLowerCase().includes(searchQuery.toLowerCase()) ||
      item.tags.some(tag => tag.toLowerCase().includes(searchQuery.toLowerCase()))
    );
    
    if (items.length > 0) {
      acc[category] = items;
    }
    
    return acc;
  }, {});

  const getCurrentItems = () => {
    if (searchQuery.trim()) {
      return filteredItems;
    }
    return { [activeCategory]: faqItems[activeCategory] };
  };

  const handleContactClick = () => {
    window.location.href = '/contact';
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-gray-100">
      {/* Hero Section */}
      <section className="py-16 bg-gradient-to-r from-blue-600 to-purple-600 text-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Foire Aux Questions</h1>
            <p className="text-xl text-blue-100 mb-8">
              Trouvez rapidement des réponses à vos questions les plus fréquentes
            </p>
            
            <div className="flex flex-wrap justify-center gap-6 mb-8">
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Clock size={20} />
                <span>Mise à jour régulière</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <Users size={20} />
                <span>+500 clients satisfaits</span>
              </div>
              <div className="flex items-center gap-2 bg-white/10 backdrop-blur-sm px-4 py-2 rounded-full">
                <MessageCircle size={20} />
                <span>Support réactif</span>
              </div>
            </div>

            {/* Search */}
            <div className="max-w-2xl mx-auto">
              <div className="relative">
                <Search className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
                <input
                  type="text"
                  placeholder="Rechercher une question ou un mot-clé..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-12 pr-4 py-3 rounded-lg border-0 focus:ring-2 focus:ring-blue-500 text-gray-900"
                />
              </div>
              <p className="text-sm text-blue-200 mt-2">
                Tapez un mot-clé comme "devis", "délais", "SEO", "maintenance"...
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-12">
        <div className="container mx-auto px-4">
          {/* Category Tabs */}
          {!searchQuery.trim() && (
            <div className="flex flex-wrap gap-2 mb-8 justify-center">
              {faqCategories.map(category => (
                <button
                  key={category.id}
                  className={`flex items-center gap-2 px-5 py-3 rounded-full transition-colors ${
                    activeCategory === category.id 
                      ? 'bg-blue-600 text-white' 
                      : 'bg-white text-gray-700 hover:bg-gray-100 shadow-sm'
                  }`}
                  onClick={() => setActiveCategory(category.id)}
                >
                  {category.icon}
                  {category.label}
                </button>
              ))}
            </div>
          )}

          {/* Search Results Header */}
          {searchQuery.trim() && (
            <div className="mb-8">
              <div className="flex items-center justify-between mb-4">
                <h2 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
                  <Search size={20} />
                  Résultats pour : "{searchQuery}"
                  <span className="text-blue-600 ml-2">
                    ({Object.values(filteredItems).reduce((sum, items) => sum + items.length, 0)} questions trouvées)
                  </span>
                </h2>
                <button 
                  onClick={() => setSearchQuery('')}
                  className="bg-gray-200 hover:bg-gray-300 text-gray-800 px-4 py-2 rounded-lg transition-colors"
                >
                  Voir toutes les catégories
                </button>
              </div>
            </div>
          )}

          {/* FAQ Items */}
          <div className="max-w-4xl mx-auto">
            {Object.keys(getCurrentItems()).map(category => (
              <div key={category} className="mb-8">
                {searchQuery.trim() && (
                  <h3 className="text-xl font-bold text-gray-900 mb-4 flex items-center gap-2">
                    {faqCategories.find(c => c.id === category)?.icon}
                    {faqCategories.find(c => c.id === category)?.label}
                  </h3>
                )}
                
                <div className="space-y-4">
                  {getCurrentItems()[category].map((item, index) => {
                    const key = `${category}-${index}`;
                    const isOpen = openItems[key];
                    
                    return (
                      <div 
                        key={key} 
                        className={`bg-white rounded-xl shadow-sm overflow-hidden ${
                          isOpen ? 'ring-2 ring-blue-500' : ''
                        }`}
                      >
                        <button
                          className="w-full px-6 py-4 text-left flex justify-between items-center hover:bg-gray-50 transition-colors"
                          onClick={() => toggleItem(category, index)}
                        >
                          <span className="font-medium text-gray-900 pr-4">{item.question}</span>
                          <ChevronDown 
                            className={`text-gray-400 transition-transform ${
                              isOpen ? 'transform rotate-180' : ''
                            }`} 
                            size={20} 
                          />
                        </button>
                        
                        <div 
                          className={`px-6 overflow-hidden transition-all duration-300 ${
                            isOpen ? 'py-4 border-t border-gray-100' : 'max-h-0 py-0'
                          }`}
                        >
                          <p className="text-gray-600 whitespace-pre-line">{item.answer}</p>
                          {item.tags && (
                            <div className="flex flex-wrap gap-2 mt-4">
                              {item.tags.map(tag => (
                                <span 
                                  key={tag} 
                                  className="px-3 py-1 bg-blue-100 text-blue-700 rounded-full text-sm"
                                >
                                  {tag}
                                </span>
                              ))}
                            </div>
                          )}
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* No Results */}
            {searchQuery.trim() && Object.keys(filteredItems).length === 0 && (
              <div className="text-center py-12">
                <Search size={48} className="text-gray-400 mx-auto mb-4" />
                <h3 className="text-2xl font-bold text-gray-900 mb-2">Aucun résultat trouvé</h3>
                <p className="text-gray-600 mb-6">
                  Aucune question ne correspond à votre recherche. Essayez avec d'autres mots-clés.
                </p>
                <div className="bg-gray-50 rounded-xl p-6 max-w-md mx-auto mb-6">
                  <p className="font-medium text-gray-900 mb-2">Suggestions :</p>
                  <ul className="text-gray-600 space-y-1 text-left">
                    <li>• Vérifiez l'orthographe</li>
                    <li>• Utilisez des termes plus généraux</li>
                    <li>• Essayez des synonymes</li>
                  </ul>
                </div>
                <button 
                  onClick={() => setSearchQuery('')}
                  className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Voir toutes les questions
                </button>
              </div>
            )}
          </div>

          {/* CTA Section */}
          <div className="max-w-4xl mx-auto mt-12">
            <div className="bg-gradient-to-r from-blue-500 to-purple-500 rounded-2xl p-8 text-white text-center">
              <MessageCircle size={48} className="mx-auto mb-4" />
              <h2 className="text-2xl font-bold mb-2">Vous ne trouvez pas votre réponse ?</h2>
              <p className="text-blue-100 mb-6">
                Notre équipe est à votre disposition pour répondre à toutes vos questions spécifiques.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <button 
                  onClick={handleContactClick}
                  className="bg-white hover:bg-gray-100 text-blue-600 px-6 py-3 rounded-lg font-medium transition-colors"
                >
                  Nous contacter
                </button>
                <a 
                  href="tel:+33758132406"
                  className="bg-blue-600/30 hover:bg-blue-600/40 text-white px-6 py-3 rounded-lg font-medium flex items-center justify-center gap-2 transition-colors"
                >
                  <Phone size={20} />
                  Appeler maintenant
                </a>
              </div>
              <p className="flex items-center justify-center gap-2 mt-6 text-blue-200">
                <Clock size={16} />
                Réponse garantie sous 24h ouvrées
              </p>
            </div>
          </div>

          {/* Additional Resources */}
          <div className="max-w-6xl mx-auto mt-16">
            <h2 className="text-3xl font-bold text-center text-gray-900 mb-8">
              Ressources supplémentaires
            </h2>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              <a href="/blog" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <BookOpen size={24} className="text-blue-600 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Blog & Articles</h3>
                <p className="text-gray-600 text-sm">
                  Découvrez nos articles sur le développement web, le marketing digital et les tendances.
                </p>
              </a>
              
              <a href="/portfolio" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <Smartphone size={24} className="text-purple-600 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Nos Réalisations</h3>
                <p className="text-gray-600 text-sm">
                  Explorez nos projets précédents et découvrez notre expertise.
                </p>
              </a>
              
              <a href="/services" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <Users size={24} className="text-green-600 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Nos Services</h3>
                <p className="text-gray-600 text-sm">
                  Découvrez l'ensemble de nos services et solutions digitales.
                </p>
              </a>
              
              <a href="/process" className="bg-white rounded-xl shadow-sm p-6 hover:shadow-md transition-shadow">
                <Clock size={24} className="text-orange-600 mb-4" />
                <h3 className="font-bold text-gray-900 mb-2">Notre Processus</h3>
                <p className="text-gray-600 text-sm">
                  Comprenez comment nous travaillons de la conception à la livraison.
                </p>
              </a>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}